/**
 * Export error reporting — the silent-but-diagnosable half of the export
 * feature's error contract:
 *
 *   - The USER sees only a generic toast ("Export failed — … ask the AI
 *     assistant"). Raw errors never reach the UI.
 *   - The full context lands in the file's debug log (`src: 'export'`), where
 *     the AI agent reads it via the `debug_log` tool and the passive
 *     recent-errors tail. The msg grammar `[export:<stage>] engine=<e>
 *     cause=<tag> … opts=… dims=…` is documented in the app-inspect skill's
 *     export-failures playbook — keep them in sync.
 *
 * PRIVACY: entries must never include document content — no HTML source, no
 * text runs, no asset URLs. Only stage/engine/cause tags, option enums,
 * dimensions, and the error message/stack (server-side redaction applies on
 * top as defence in depth).
 */
import { debugLogApi } from '@papan/files/client'
import type { PdfExportOptions } from '../ui/PdfExportDialog.js'

export type ExportStage =
  | 'snapshot'   // requestSnapshot of the live preview failed/timed out
  | 'capture'    // capture-session iframe setup / settle
  | 'preview'    // dialog preview rendering
  | 'rasterize'  // engine capture (snapdom / html2canvas-pro)
  | 'assemble'   // PDF/image assembly (jsPDF, compositing, encoding)
  | 'fonts'      // Noto font fetch/registration
  | 'deliver'    // download anchor / save-to-drive handoff

export type ExportEngine = 'snapdom' | 'html2canvas-pro'

export type ExportCause =
  | 'canvas-limit'      // canvas dimension/area cap exceeded (very tall docs)
  | 'tainted-canvas'    // cross-origin image without CORS tainted the canvas
  | 'svg-decode'        // SVG-image decode failure (tall-doc foreignObject path)
  | 'color-parse'       // rasterizer choked on a color function
  | 'font-fetch'        // Noto font CDN unreachable/blocked
  | 'rasterize-timeout' // engine exceeded its time budget
  | 'snapshot-timeout'  // live-preview snapshot didn't settle in time
  | 'encode-fail'       // canvas.toBlob returned null / encode error
  | 'unknown'

export interface ExportErrorCtx {
  fileId: string
  engine?: ExportEngine
  /** Options snapshot — enum/number values only, never content. */
  options?: Partial<PdfExportOptions>
  /** 'warn' = degraded but the export continued (e.g. engine fallback). */
  level?: 'error' | 'warn'
  /** Content dimensions at failure time, when known. */
  dims?: { contentHeight?: number; captureWidth?: number; pageCount?: number }
}

/**
 * Typed error for failures OUR code raises. Carries the cause (and optionally
 * the stage) as data, so classification of first-party failures is exact
 * instead of re-parsing our own message strings — the string heuristics in
 * classifyExportError below remain only for third-party errors (snapdom,
 * html2canvas, DOMExceptions). This already bit once: on 2026-07-14 the engine
 * cap messages didn't contain "canvas" and were tagged `unknown`.
 */
export class ExportFault extends Error {
  readonly exportCause: ExportCause
  readonly exportStage?: ExportStage
  constructor(message: string, cause: ExportCause, stage?: ExportStage) {
    super(message)
    this.name = 'ExportFault'
    this.exportCause = cause
    this.exportStage = stage
  }
}

/** The stage a failure belongs to, when the error carries one (ExportFault);
 *  `fallback` otherwise. Lets terminal reports name the real failing phase
 *  instead of hardcoding the outermost one. */
export function exportErrorStage(error: unknown, fallback: ExportStage): ExportStage {
  return error instanceof ExportFault && error.exportStage ? error.exportStage : fallback
}

/** Classify a failure into an actionable cause tag (see ExportCause). */
export function classifyExportError(error: unknown): ExportCause {
  if (error instanceof ExportFault) return error.exportCause
  const m = (error instanceof Error ? `${error.name}: ${error.message}` : String(error)).toLowerCase()
  if (m.includes('unsupported color function') || m.includes('attempting to parse an unsupported color')) return 'color-parse'
  // Cross-origin image without CORS taints the canvas → toBlob/getImageData
  // throw `SecurityError`. Catch it BEFORE the toblob/encode check below, which
  // would otherwise swallow it as a generic encode-fail. (The DOMException name
  // is "SecurityError" — the old `insecurityerror` substring never matched it.)
  if (m.includes('securityerror') || m.includes('tainted') || (m.includes('cross-origin') && m.includes('canvas'))) return 'tainted-canvas'
  if (m.includes('toblob') || m.includes('encode')) return 'encode-fail'
  if (m.includes('failed to fetch notosans') || m.includes('gstatic') || (m.includes('font') && m.includes('fetch'))) return 'font-fetch'
  if (m.includes('snapshot') && m.includes('timeout')) return 'snapshot-timeout'
  if (m.includes('timeout') || m.includes('timed out')) return 'rasterize-timeout'
  if (m.includes('svg') && (m.includes('decode') || m.includes('load'))) return 'svg-decode'
  // "Too tall to raster" — the single most common export failure. The two
  // engine messages ("content height Npx exceeds SVG-image safety cap",
  // html2canvas-pro's "Dimensions exceed maximum allowed (32767px)") don't
  // contain the literal word "canvas", so a canvas-only match tagged them
  // `unknown` and defeated the taxonomy (2026-07-14). Match on the shape
  // instead: an "exceed"/"maximum"/"safety cap" paired with a size dimension.
  if (
    m.includes('safety cap') ||
    m.includes('maximum allowed') ||
    ((m.includes('exceed') || m.includes('limit') || m.includes('memory') || m.includes('too large')) &&
      (m.includes('canvas') || m.includes('dimension') || m.includes('height') || m.includes('width') || m.includes('area'))) ||
    m.includes('invalidstateerror')
  ) return 'canvas-limit'
  return 'unknown'
}

/**
 * Map a cause to a SHORT i18n key suffix (`export.reason.<suffix>`) for a
 * user-facing hint in the failure toast — enough to tell them roughly what's
 * wrong without a system-level detail. Returns null for causes with no useful
 * lay description (the toast falls back to the fully generic message).
 */
export function describeExportCause(cause: ExportCause): string | null {
  switch (cause) {
    case 'canvas-limit':
    case 'encode-fail':
    case 'svg-decode':
      return 'tooLarge'
    case 'rasterize-timeout':
      return 'tooComplex'
    case 'snapshot-timeout':
      return 'notLoaded'
    case 'tainted-canvas':
      return 'blockedImage'
    default:
      return null
  }
}

/** Options keys safe to serialize into the log (enum/number values only). */
const SAFE_OPTION_KEYS: (keyof PdfExportOptions)[] = [
  'format', 'pageSize', 'orientation', 'bgColor', 'bgFill', 'fitToPage',
  'jpegQuality', 'bgAlpha', 'captureWidth',
]

function snapshotOptions(options?: Partial<PdfExportOptions>): string {
  if (!options) return ''
  const out: Record<string, unknown> = {}
  for (const k of SAFE_OPTION_KEYS) {
    if (options[k] !== undefined) out[k] = options[k]
  }
  return JSON.stringify(out)
}

// One reporting session per export-dialog open; also the flood-control unit.
let sessId = ''
let sessCount = 0
let sessMarks: Record<string, number | string> = {}
let sessT0 = 0
const MAX_ENTRIES_PER_SESSION = 10

/** Start a new export reporting session (call when the export dialog opens).
 *  Returns the session id (also used to correlate entries in the log). */
export function beginExportSession(): string {
  sessId = `export-${Math.random().toString(16).slice(2, 10)}`
  sessCount = 0
  sessMarks = {}
  sessT0 = performance.now()
  return sessId
}

/** performance.now() at beginExportSession — lets later stages report time
 *  spent before their own clock started (dialog mount, snapshot round trip). */
export function exportSessionT0(): number {
  return sessT0
}

/** Record a stage timing/label for the current session; flushed into the
 *  `[export:preview]` breadcrumb. Values: ms (rounded) or a short enum. */
export function markExportStage(key: string, value: number | string): void {
  sessMarks[key] = typeof value === 'number' ? Math.round(value) : value
}

/**
 * One info breadcrumb when the dialog preview first becomes visible, carrying
 * per-stage timings (snapshot/session/raster + path). This is what makes a
 * "the preview takes N seconds" report diagnosable after the fact: the debug
 * log pinpoints WHICH stage ate the time, instead of a blind repro hunt.
 * Timings only — never content.
 */
export function reportPreviewReady(fileId: string, t: { sessionMs: number; rasterMs: number; totalMs: number }): void {
  try {
    const extra = Object.entries(sessMarks).map(([k, v]) => `${k}=${v}`).join(' ')
    const msg = `[export:preview] ready in ${Math.round(t.totalMs)}ms session=${Math.round(t.sessionMs)}ms raster=${Math.round(t.rasterMs)}ms${extra ? ` ${extra}` : ''}`
    appendEntry(fileId, 'info', msg)
  } catch { /* never throw */ }
}

function buildMsg(stage: ExportStage, cause: ExportCause | null, message: string, ctx: ExportErrorCtx): string {
  const parts = [`[export:${stage}]`]
  if (ctx.engine) parts.push(`engine=${ctx.engine}`)
  if (cause) parts.push(`cause=${cause}`)
  parts.push(message)
  const opts = snapshotOptions(ctx.options)
  if (opts && opts !== '{}') parts.push(`opts=${opts}`)
  if (ctx.dims) {
    const d = ctx.dims
    const dims = [d.contentHeight != null ? `h=${Math.round(d.contentHeight)}` : '',
      d.captureWidth != null ? `w=${Math.round(d.captureWidth)}` : '',
      d.pageCount != null ? `pages=${d.pageCount}` : ''].filter(Boolean).join(',')
    if (dims) parts.push(`dims=${dims}`)
  }
  return parts.join(' ')
}

function appendEntry(fileId: string, lvl: 'error' | 'warn' | 'info', msg: string, stack?: string): void {
  if (!fileId) return
  if (!sessId) beginExportSession()
  if (sessCount >= MAX_ENTRIES_PER_SESSION) return
  sessCount++
  // Fire-and-forget: reporting must never break (or block) the export flow.
  // 403 (read-only viewer), offline, etc. are all deliberately swallowed —
  // the console.error above remains the fallback signal.
  debugLogApi.append('html', fileId, [{ ts: Date.now(), sess: sessId, src: 'export', lvl, msg, stack }])
    .catch(() => {})
}

/**
 * Report an export failure (or degradation, with level:'warn') to the console
 * and the file's debug log. Never throws.
 */
export function reportExportError(stage: ExportStage, error: unknown, ctx: ExportErrorCtx): void {
  try {
    let cause = classifyExportError(error)
    // Stage-informed bias: a generic network failure ("TypeError: Failed to
    // fetch") in the fonts stage IS a font fetch problem — the message alone
    // can't tell, but the stage can (seen live: CORS-blocked gstatic logged
    // as cause=unknown, sending the playbook down the wrong row).
    if (cause === 'unknown' && stage === 'fonts') cause = 'font-fetch'
    const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
    console.error('[export]', stage, ctx.engine ?? '', error)
    appendEntry(
      ctx.fileId,
      ctx.level ?? 'error',
      buildMsg(stage, cause, message, ctx),
      error instanceof Error ? error.stack : undefined,
    )
  } catch {
    // Reporting must never throw into the export flow.
  }
}

/** Breadcrumb on successful export — lets the assistant see which engine
 *  served the user and diagnose "it worked before" regressions. */
export function reportExportSuccess(ctx: ExportErrorCtx & { pages: number; ms: number; bytes?: number }): void {
  try {
    const opts = snapshotOptions(ctx.options)
    // bytes makes size regressions (e.g. a quality-setting pathology) visible
    // in the debug log — sizes are not content, so this stays privacy-safe.
    const msg = `[export:done] engine=${ctx.engine ?? 'unknown'} exported ${ctx.pages} page(s) in ${Math.round(ctx.ms)}ms${ctx.bytes != null ? ` bytes=${ctx.bytes}` : ''}${opts && opts !== '{}' ? ` opts=${opts}` : ''}`
    appendEntry(ctx.fileId, 'info', msg)
  } catch { /* never throw */ }
}
